// src/pages/task/TaskDetailPage.tsx

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useTask, useI18n } from '@react/hooks';
import { entityStore } from '@/stores/EntityStore';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Alert,
  Badge,
  ErrorAlert,
} from '@react/components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  taskStatusMap,
  taskPriorityMap,
  getStatusMapping,
  formatDateTime,
  formatDuration,
  formatEnumLabel,
  getDisplayName,
  getDueDateUrgency,
  getPriorityIconPath,
  getPriorityColor,
  type BadgeVariant,
} from '@/features/task/logic';
import { TaskTimeTracking } from '@/features/task/TaskTimeTracking';
import { TaskDependencies } from '@/features/task/TaskDependencies';
import { TaskChecklists } from '@/features/task/TaskChecklists';
import { TaskStatusControl } from '@/features/task/TaskStatusControl';
import { TaskAssigneeControl } from '@/features/task/TaskAssigneeControl';
import { TaskWorkPlanning } from '@/features/task/TaskWorkPlanning';
import { TaskCustomFieldsPanel } from '@/features/task/TaskCustomFieldsPanel';
import { TaskWatchers } from '@/features/task/TaskWatchers';
import { TaskSubtasks } from '@/features/task/TaskSubtasks';
import { TaskAttachments } from '@/features/task/TaskAttachments';
import { TaskActivityFeed } from '@/features/task/TaskActivityFeed';
import { TaskAutomationPanel } from '@/features/task/TaskAutomationPanel';

// --- Inline SVG icons ---

function EditIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function MoreIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="1" /><circle cx="12" cy="5" r="1" /><circle cx="12" cy="19" r="1" />
    </svg>
  );
}

function mapStatusToVariant(status: string): BadgeVariant {
  return getStatusMapping(taskStatusMap, status).variant;
}

function mapPriorityToVariant(priority: string): BadgeVariant {
  return getStatusMapping(taskPriorityMap, priority).variant;
}

export const TaskDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useTask();
  const { t } = useI18n();
  const item = store.currentItem as any;

  const [showActions, setShowActions] = useState(false);

  const refresh = () => { if (id) store.GetTask(id); };

  useEffect(() => {
    if (id) {
      store.GetTask(id);
    }
    return () => { store.clearCurrent(); entityStore.clearContext(); };
  }, [id]);

  // Load entity context for Right Rail
  useEffect(() => {
    if (item?.entity) {
      entityStore.loadEntityData({
        urn: item.entity.urn || '',
        type: item.entity.type_name || 'task',
        id: item.entity.id || id!,
        title: item.title || '',
      });
      if (item.aspects?.items) {
        entityStore.setAspects(item.aspects.items);
      }
    }
  }, [item?.entity?.id]);

  const handleDelete = async () => {
    if (!id || !confirm(t('task.deleteConfirm'))) return;
    try {
      await store.DeleteTask(id);
      navigate('/tasks');
    } catch { /* store handles error */ }
  };

  // Loading — only block on the page-level task fetch. `store.isLoading` is
  // the generic flag flipped by every store action, including child components
  // like CustomFieldsCard; gating the page on it caused an unmount/remount
  // loop where each remount fired the child's effect, which set isLoading
  // again, etc. (the "custom-fields request flood" symptom).
  if (store.isLoadingItem && !item) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse space-y-4 w-full max-w-2xl">
          <div className="h-8 bg-muted rounded w-1/3" />
          <div className="h-4 bg-muted rounded w-1/2" />
          <div className="h-64 bg-muted rounded" />
        </div>
      </div>
    );
  }

  // Error
  if (store.error && !item) {
    return (
      <>
        <ErrorAlert error={store.error} />
        <Link to="/tasks" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </>
    );
  }

  // Not found
  if (!item) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link to="/tasks" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </Alert>
    );
  }

  const task = item.task || {};
  const status = task.status || '';
  const priority = task.priority || '';
  const assignee = task.assignee;
  const dueDate = task.due_date;
  const taskChecklists = item.checklists?.checklists || item.checklists || [];
  const dueDateUrgency = getDueDateUrgency(dueDate, status);
  const priorityIconPath = getPriorityIconPath(priority);
  const priorityColorClass = getPriorityColor(priority);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <nav className="text-sm mb-3">
          <Link to="/tasks" className="text-muted-foreground hover:text-foreground">
            {t('task.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{String(item.title ?? t('task.details'))}</span>
        </nav>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-foreground">{String(item.title ?? '')}</h1>
            {status && (
              <Badge variant={mapStatusToVariant(status)} size="sm">
                {formatEnumLabel(status, 'TASK_STATUS_')}
              </Badge>
            )}
            {priority && (
              <Badge variant={mapPriorityToVariant(priority)} size="sm">
                {formatEnumLabel(priority, 'TASK_PRIORITY_')}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Link to={`/tasks/${id}/edit`}>
              <Button variant="secondary" size="sm">
                <EditIcon className="h-4 w-4 mr-1.5" />
                {t('common.edit')}
              </Button>
            </Link>
            {/* More actions */}
            <div className="relative">
              <Button variant="ghost" size="sm" onClick={() => setShowActions(!showActions)}>
                <MoreIcon className="h-4 w-4" />
              </Button>
              {showActions && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowActions(false)} aria-hidden="true" />
                  <div className="absolute right-0 top-full mt-1 w-48 bg-popover border border-border rounded-md shadow-lg z-20 py-1">
                    <button
                      onClick={() => { handleDelete(); setShowActions(false); }}
                      className="w-full text-left px-3 py-2 text-sm text-destructive hover:bg-muted"
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Main grid: content + sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column — main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card>
            <CardHeader>
              <CardTitle>{t('task.field.description')}</CardTitle>
            </CardHeader>
            <CardContent>
              {item.description ? (
                <div className="prose prose-sm dark:prose-invert max-w-none text-foreground">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {String(item.description)}
                  </ReactMarkdown>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t('task.noDescription')}</p>
              )}
            </CardContent>
          </Card>

          {/* Checklists — always shown (empty state + add checklist) */}
          {id && (
            <TaskChecklists
              taskId={id}
              checklists={taskChecklists}
              onChanged={refresh}
            />
          )}

          {/* Subtasks — quick-add + progress summary */}
          {id && (
            <TaskSubtasks
              parentId={id}
              parentTypeName={item.entity?.type_name || 'task'}
            />
          )}

          {/* Time tracking — log / list / delete + running total */}
          {id && <TaskTimeTracking taskId={id} />}

          {/* Dependencies — add / remove + link to graph */}
          {id && <TaskDependencies taskId={id} />}

          {/* Attachments — files linked to this task */}
          <TaskAttachments />

          {/* Activity — loading / empty / error + load more */}
          {id && <TaskActivityFeed taskId={id} />}
        </div>

        {/* Right sidebar */}
        <div className="space-y-4">
          {/* Status — prominent, top of rail. Only legal transitions
              (GetAvailableTransitions) — respects requires_comment and custom
              statuses. This is the single status control on the page; the
              header no longer offers a second, illegal-transition-capable one. */}
          {id && (
            <TaskStatusControl
              taskId={id}
              currentStatus={status}
              currentCustomStatus={task.custom_status}
              onChanged={refresh}
            />
          )}

          {/* A task's scheduling lives in Work planning below, which speaks the
              task service's own Schedule/Reschedule/Suggest RPCs. The calendar
              module's generic entity-scheduling panel used to sit here too,
              giving one task two competing "schedule" cards — the generic one
              could only report that scheduling was unavailable. */}

          {/* Work planning — schedule the work in the assignee's calendar */}
          {id && (
            <TaskWorkPlanning
              taskId={id}
              scheduledStart={task.scheduled_work?.start_time || task.calendar?.start_time}
              scheduledEnd={task.scheduled_work?.end_time || task.calendar?.end_time}
              onChanged={refresh}
            />
          )}

          {/* Priority */}
          <Card>
            <CardContent className="p-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">{t('task.field.priority')}</p>
              <div className="flex items-center gap-2">
                {priorityIconPath && (
                  <svg className={`h-4 w-4 ${priorityColorClass}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <path d={priorityIconPath} />
                  </svg>
                )}
                {priority ? (
                  <Badge variant={mapPriorityToVariant(priority)} size="sm">
                    {formatEnumLabel(priority, 'TASK_PRIORITY_')}
                  </Badge>
                ) : (
                  <span className="text-sm text-muted-foreground">--</span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Assignee — assign / reassign / clear */}
          {id && (
            <TaskAssigneeControl
              taskId={id}
              assignee={assignee}
              onChanged={refresh}
            />
          )}

          {/* Due Date */}
          <Card>
            <CardContent className="p-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">{t('task.field.dueDate')}</p>
              <span className={`text-sm font-medium ${
                dueDateUrgency === 'overdue' ? 'text-destructive' :
                dueDateUrgency === 'soon' ? 'text-yellow-600 dark:text-yellow-400' :
                'text-foreground'
              }`}>
                {formatDateTime(dueDate)}
              </span>
              {dueDateUrgency === 'overdue' && (
                <Badge variant="error" size="sm" className="ml-2">{t('task.overdue')}</Badge>
              )}
            </CardContent>
          </Card>

          {/* Progress */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('task.progress')}</p>
                <span className="text-sm font-medium text-foreground">{task.progress_percent || 0}%</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all"
                  style={{ width: `${Math.min(100, Math.max(0, task.progress_percent || 0))}%` }}
                />
              </div>
            </CardContent>
          </Card>

          {/* Duration */}
          {(task.estimated_duration || task.actual_duration) && (
            <Card>
              <CardContent className="p-4 space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('task.duration')}</p>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('task.estimated')}</span>
                  <span className="text-foreground">{formatDuration(task.estimated_duration)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('task.actual')}</span>
                  <span className="text-foreground">{formatDuration(task.actual_duration)}</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Dates */}
          <Card>
            <CardContent className="p-4 space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('task.dates')}</p>
              {task.start_date && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('task.startDate')}</span>
                  <span className="text-foreground">{formatDateTime(task.start_date)}</span>
                </div>
              )}
              {task.completed_at && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('task.completedAt')}</span>
                  <span className="text-foreground">{formatDateTime(task.completed_at)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('common.createdAt')}</span>
                <span className="text-foreground">{formatDateTime(item.created_at)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('common.updatedAt')}</span>
                <span className="text-foreground">{formatDateTime(item.updated_at)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Type & Creator */}
          <Card>
            <CardContent className="p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('task.field.type')}</span>
                <span className="text-foreground">{item.type ? formatEnumLabel(String(item.type), 'TASK_TYPE_') : '--'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('task.creator')}</span>
                <span className="text-foreground">{getDisplayName(item.creator)}</span>
              </div>
              {item.parent && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('task.parent')}</span>
                  <Link
                    to={item.parent.type_name === 'project'
                      ? `/projects/${item.parent.id}`
                      : `/tasks/${item.parent.id}`}
                    className="text-primary hover:underline text-xs"
                  >
                    {item.parent.title
                      || item.parent.display_name
                      || (item.parent.type_name
                        ? `${formatEnumLabel(String(item.parent.type_name), '')} #${String(item.parent.id).substring(0, 8)}`
                        : String(item.parent.id).substring(0, 8) + '...')}
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tags */}
          {task.tags && task.tags.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">{t('task.field.tags')}</p>
                <div className="flex flex-wrap gap-1.5">
                  {task.tags.map((tag: string, idx: number) => (
                    <Badge key={idx} variant="secondary" size="sm">{tag}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Labels */}
          {task.labels?.labels && task.labels.labels.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">{t('task.labels')}</p>
                <div className="flex flex-wrap gap-1.5">
                  {task.labels.labels.map((label: any) => (
                    <span
                      key={label.id}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                      style={{
                        backgroundColor: label.color ? `${label.color}20` : undefined,
                        color: label.color || undefined,
                        border: `1px solid ${label.color || 'currentColor'}`,
                      }}
                    >
                      {label.name}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Custom Fields — values + definitions, set / clear */}
          {id && (
            <TaskCustomFieldsPanel taskId={id} editable />
          )}

          {/* Watchers — watch/unwatch self, add/remove any watcher */}
          {id && (
            <TaskWatchers
              taskId={id}
              watchers={task.watchers?.actors || []}
              onChanged={refresh}
            />
          )}

          {/* External Reference */}
          {item.external_ref && (
            <Card>
              <CardContent className="p-4">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">{t('task.externalRef')}</p>
                {item.external_ref.external_url ? (
                  <a
                    href={item.external_ref.external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline"
                  >
                    {item.external_ref.system}: {item.external_ref.external_id}
                  </a>
                ) : (
                  <p className="text-sm text-foreground">
                    {item.external_ref.system || ''}: {item.external_ref.external_id || ''}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Conversation */}
          {item.conversation?.id && (
            <Card>
              <CardContent className="p-4">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">{t('task.conversation')}</p>
                <Link to={`/chats/${item.conversation.id}`} className="text-sm text-primary hover:underline">
                  {t('task.conversationLink')}
                </Link>
              </CardContent>
            </Card>
          )}

          {/* Workflow — active automations that can run for this task (see
              docs/design/workspace-redesign/08-workflow-integration.md). Read-only
              pointer into the workflow product; collapsed by default. */}
          <TaskAutomationPanel />
        </div>
      </div>
    </div>
  );
});

export default TaskDetailPage;
